// Grapple hook
export const GRAPPLE = {
  // Ammo
  STARTING_AMMO: 3,

  // Hook travel
  HOOK_SPEED: 30,
  HOOK_GRAVITY: 0.06,
  HOOK_RADIUS: 6,
  MAX_RANGE: 1000,
  COLLISION_SUBSTEPS: 6,

  // Verlet rope
  ROPE_SEGMENTS: 12,
  CONSTRAINT_ITERATIONS: 3,
  ROPE_GRAVITY: 30,
  ROPE_DAMPING: 0.99,

  // Retraction
  RETRACT_SPEED: 8,
  PULL_STRENGTH: 0.6,
  MAX_PULL_SPEED: 14,
  ARRIVAL_DISTANCE: 35,

  // Launch (at anchor arrival)
  LAUNCH_UPWARD_IMPULSE: -8,
  LAUNCH_FORWARD_MULT: 0.5,
  LAUNCH_MIN_VY: -6,

  // Retraction timeout
  MAX_RETRACTION_TIME: 300,    // frames (~5s at 60fps) — auto-cancel if player stuck

  // Retraction physics — floaty traversal over jagged edges
  RETRACT_WALL_BOUNCE: 0.65,    // wall bounce retention during pull (vs 0.4 normal)
  RETRACT_GROUND_POP: 1.2,      // upward impulse on ground contact during pull (px/frame)

  // Air control during/after
  AIR_CONTROL_ACCEL: 0.12,
  AIR_CONTROL_MAX_SPEED: 2.5,

  // Rope physics
  HOOK_SPAWN_BUFFER: 2,         // px buffer between tank edge and hook spawn
  CONSTRAINT_EPSILON: 0.001,    // minimum distance before skipping constraint solve
  TERRAIN_PUSH_DIST: 4,         // px push distance for rope-terrain collision
  LAUNCH_VY_RETENTION: 0.3,     // vy multiplier on anchor arrival launch
  MIN_REST_LENGTH: 5,           // minimum rest length for rope segments during retraction
  EMBED_BOUNCE_RETENTION: 0.55, // vx retention on terrain embed bounce during retraction
} as const;

// Grapple visual effects
export const GRAPPLE_FX = {
  // Hook launch
  LAUNCH_SPARK_COUNT: 8,
  LAUNCH_SHAKE: 3,
  LAUNCH_SHAKE_DECAY: 0.7,

  // Hook attach
  ATTACH_SPARK_COUNT: 12,
  ATTACH_SHAKE: 6,
  ATTACH_SHAKE_DECAY: 0.8,
  ATTACH_FLASH_ALPHA: 0.12,

  // Rope scrape (rope touching terrain)
  SCRAPE_SPARK_RATE: 0.25,

  // Anchor arrival / launch
  ARRIVAL_SPARK_COUNT: 16,
  ARRIVAL_SHAKE: 10,
  ARRIVAL_SHAKE_DECAY: 0.8,
  ARRIVAL_FLASH_ALPHA: 0.15,

  // Rope rendering
  ROPE_WIDTH: 3,
  ROPE_GLOW_WIDTH: 8,
  ROPE_FADE_DURATION: 0.3,

  // Hook travel trail
  TRAIL_RATE: 2,
  TRAIL_LIFE: 0.15,
} as const;

// Muzzle flash — directional launch effect at turret tip
export const MUZZLE = {
  FLASH_COUNT: 8,
  CONE_COUNT: 22,
  CONE_SPREAD: Math.PI / 5,     // ±36° forward cone
  SPARK_COUNT: 16,
  SPARK_SPREAD: Math.PI / 3,    // ±60° wider cone
  SMOKE_COUNT: 8,
  SMOKE_DELAY: 35,              // ms
  SHOCKWAVE_RADIUS: 45,
  IMPACT_LINE_COUNT: 5,
  IMPACT_LINE_SPREAD: Math.PI / 10,  // ±18°
  SCREEN_FLASH_ALPHA: 0.10,
  SHAKE_INTENSITY: 12,
  SHAKE_DECAY: 0.82,
} as const;

// Trajectory preview
export const TRAJECTORY = {
  MAX_STEPS: 400,
  DOT_SPACING: 12,     // virtual px between dots
  DOT_RADIUS: 3,
  BASE_ALPHA: 0.4,
  FADE_START: 0.6,     // fraction of dots before fade begins
} as const;

// Duelist Slash — Shot 1: iaijutsu dash-strike, charge determines dash distance + damage
export const DUELIST_SLASH = {
  CHARGE_RATE: 0.025,           // ~40 frames (0.67s) to full charge
  // Charge tier thresholds
  MID_THRESHOLD: 0.33,
  LONG_THRESHOLD: 0.66,
  // Dash distance per tier (px)
  SHORT_RANGE: 140,
  MID_RANGE: 240,
  LONG_RANGE: 360,
  // Dash duration per tier (frames)
  SHORT_FRAMES: 8,
  MID_FRAMES: 10,
  LONG_FRAMES: 12,
  // Damage per tier
  SHORT_DAMAGE: 9,
  MID_DAMAGE: 12,
  LONG_DAMAGE: 16,
  // Knockback multiplier per tier (applied on top of weapon knockbackMult)
  SHORT_KB: 0.09,
  MID_KB: 0.11,
  LONG_KB: 0.14,
  // Phase timing
  STARTUP_FRAMES: 3,            // committed startup before dash
  PAUSE_FRAMES: 40,             // dramatic pause on hit (~667ms)
  WHIFF_FRAMES: 15,             // recovery on miss
  RESOLVE_HITLAG_MS: 350,       // real hitlag on slash resolve
  WHIFF_FREEZE_MS: 100,         // brief stutter on whiff
  // Hit detection
  HIT_PERPENDICULAR: 40,        // px from dash line center to count as hit
  // Terrain carve
  CARVE_RADIUS: 40,             // blade slice through terrain each dash frame (cf. Trencher 55)
  // Legacy compat (used by weapon registration)
  BASE_DAMAGE: 9,
  KB_MULT: 1.0,
} as const;

// Launcher — Homing Missile (Shot 1): Lob → Lock → Hypersonic Dive
export const HOMING_MISSILE = {
  // Phase 1: Lob — ballistic arc upward, no tracking
  LOB_FRAMES: 40,            // ~0.67s of rising arc
  LOB_GRAVITY: 0.06,         // light gravity — floats up high
  // Phase 2: Lock — brief pause as target is acquired
  LOCK_FRAMES: 18,           // ~0.3s hover/acquisition window
  LOCK_GRAVITY: 0.01,        // near-zero — hangs in the air
  // Phase 3: Dive — hypersonic burst straight to target
  DIVE_SPEED: 14,            // fast straight-line to target
  DIVE_GRAVITY: 0.0,         // zero — pure ballistic line
  // General
  LIFETIME: 210,             // 3.5s total (lob 40 + lock 18 + dive up to 152)
  WIND_RESIST: 0.5,          // counteract 50% wind during lob
  TRAIL_INTERVAL: 3,         // smoke trail frequency
} as const;

// Launcher — Gravity Bomb (Shot 2)
export const LAUNCHER_GRAV_BOMB = {
  WIND_RESIST: 0.6,       // counteract 60% of wind
  MIN_DAMAGE: 18,   MAX_DAMAGE: 65,
  MIN_BLAST: 60,    MAX_BLAST: 200,
  MIN_KB: 0.05,     MAX_KB: 0.15,
} as const;

